import { IonContent, IonPage, IonSpinner, useIonToast } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Header from "src/components/Header";
import { Etiqueta } from "src/components/SolicitudCapacitacionCampos";
import { SituacionChip } from "src/pages/SolicitudesGafcore";
import { MantenimientosAPI, SolicitudMantenimientoDetalle as Detalle } from "src/services/mantenimientos";
import { fmtFechaHoraTexto } from "src/services/traslados";

const VOLVER = "/gafcore/mantenimientos?tab=mis";

const caja: React.CSSProperties = {
  background: "#f5f5f7",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 14,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const aviso = (fondo: string, borde: string): React.CSSProperties => ({
  background: fondo,
  borderLeft: `4px solid ${borde}`,
  borderRadius: 6,
  padding: "10px 12px",
  marginBottom: 16,
  fontSize: 14,
});

const Dato: React.FC<{ etiqueta: string; valor?: React.ReactNode }> = ({ etiqueta, valor }) => (
  <div style={{ marginBottom: 12 }}>
    <Etiqueta texto={etiqueta} />
    <div style={{ fontSize: 14, color: "#333" }}>{valor || "-"}</div>
  </div>
);

/**
 * Detalle de una solicitud de mantenimiento propia (/gafcore/mantenimientos/:id).
 * EMSYS consulta GafCORE al abrirla: se ve el estado del momento.
 */
const SolicitudMantenimientoDetalle: React.FC = () => {
  const [present] = useIonToast();
  const { id } = useParams<{ id: string }>();

  const [s, setS] = useState<Detalle | null>(null);
  const [cargando, setCargando] = useState(true);

  const avisar = useCallback(
    (message: string, color = "warning") => present({ message, duration: 3500, color }),
    [present]
  );

  useEffect(() => {
    const solicitudId = Number(id);
    if (!solicitudId) return;
    setCargando(true);
    MantenimientosAPI.detalle(solicitudId)
      .then((r) => setS(r.data.data))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudo obtener la solicitud", "danger"))
      .finally(() => setCargando(false));
  }, [id, avisar]);

  return (
    <IonPage>
      <Header titulo={`Mantenimiento N° ${id}`} botonIzq={{ icono: arrowBackOutline, url: VOLVER }} />
      <IonContent id="gafcore-mantenimiento-detalle">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          {cargando ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : !s ? (
            <p style={{ color: "#777" }}>La solicitud no existe.</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <SituacionChip etiqueta={s.situacion_label} color={s.situacion_color} />
                {s.paro_equipo && <SituacionChip etiqueta="Equipo parado" color="#c0392b" />}
                {s.gaf_codigo && <span style={{ fontSize: 13, color: "#777" }}>{s.gaf_codigo}</span>}
              </div>

              {(s.envio_estado === "ERROR" || s.envio_estado === "PENDIENTE") && (
                <div style={aviso("#fff8e6", "#f0a202")}>
                  La solicitud quedó registrada pero todavía no llegó a GafCORE. Se reenvía sola.
                </div>
              )}
              {s.envio_estado === "RECHAZADA" && (
                <div style={aviso("#fdecea", "#c0392b")}>GafCORE rechazó la solicitud: {s.envio_error}</div>
              )}
              {s.sin_conexion && s.envio_estado === "ENVIADA" && (
                <div style={aviso("#fff8e6", "#f0a202")}>
                  GafCORE no respondió: se muestra el último estado conocido.
                </div>
              )}
              {s.motivo_cierre && (
                <div style={aviso("#f4f4f4", "#95a5a6")}>
                  <strong>Motivo de cierre</strong>
                  <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{s.motivo_cierre}</div>
                </div>
              )}
              {s.duplicada_de && (
                <div style={aviso("#f4f4f4", "#6c7a89")}>
                  GAF la marcó como duplicada de la solicitud <strong>{s.duplicada_de}</strong>.
                </div>
              )}
              {s.otras_abiertas.length > 0 && !s.cerrado && (
                <div style={aviso("#eaf4fb", "#3498db")}>
                  Este equipo ya tenía otro pedido en curso ({s.otras_abiertas.join(", ")}). GAF decide si es la misma falla.
                </div>
              )}
              {s.fuera_del_patrimonio && (
                <div style={aviso("#fff8e6", "#f0a202")}>
                  En GafCORE el equipo figura dado de baja o vendido. GAF la revisa antes de mandar un técnico.
                </div>
              )}

              <Dato
                etiqueta="Equipo"
                valor={`${s.codigo_g}${s.cod_fracttal ? ` · ${s.cod_fracttal}` : ""} · ${s.activo_nombre || "-"}`}
              />
              <Dato etiqueta="Dónde está" valor={s.lugar || "Donde figura en GafCORE"} />
              {s.ubicacion_coincide === false && (
                <small style={{ color: "#b58100", display: "block", marginTop: -8, marginBottom: 12 }}>
                  En GafCORE figuraba en {s.ubicacion_segun_gafcore || "otro lugar o sin ubicación"}. El técnico va al
                  lugar indicado.
                </small>
              )}
              <Dato etiqueta="Falla desde" valor={s.fecha_incidente ? fmtFechaHoraTexto(s.fecha_incidente) : "Sin indicar"} />
              <div style={{ marginBottom: 16 }}>
                <Etiqueta texto="Qué le pasa" />
                <div style={caja}>{s.descripcion}</div>
              </div>

              {s.foto && (
                <div style={{ marginBottom: 16 }}>
                  <Etiqueta texto="Foto" />
                  <img src={s.foto} alt="Foto de la falla" style={{ maxWidth: "100%", borderRadius: 8 }} />
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <Etiqueta texto="Seguimiento" />
                <div style={{ fontSize: 13, color: "#444", marginBottom: 4 }}>
                  <strong>Creada:</strong> {fmtFechaHoraTexto(s.creado_en)}
                </div>
                {/* Los hitos ya vienen como dd/mm/aaaa hh:mm (hora de Paraguay). */}
                {s.hitos.map((h) => (
                  <div key={h.etiqueta} style={{ fontSize: 13, color: "#444", marginBottom: 4 }}>
                    <strong>{h.etiqueta}:</strong> {h.fecha || "-"}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudMantenimientoDetalle;
